import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { radius } from '@karehero/llama/theme/radius';

import {
  Box,
  Chip,
  Link,
  MenuItem,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';

import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import { type Viewport } from '@/designs-dev/device/viewport';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import StateSelector from '@/designs-dev/inspect/StateSelector';
import DesktopShell from '@/karehero-app/shell/DesktopShell';
import AaFamilyClaim, {
  type Frame,
} from '@/james-playground/aa-family-claim/AaFamilyClaim';
import {
  AA_SECTIONS,
  aaScenes,
  sceneForFrame,
} from '@/james-playground/aa-family-claim/AaFamilyClaim.scenes';
import { STAGE } from '@/james-playground/aa-family-claim/AaFamilyClaim.content';
import { useScreenSelect } from '@/designs-dev/loft/ScreenSelectContext';
import {
  SCENARIOS,
  type ScenarioId,
} from '@/james-playground/aa-family-claim/claim/scenarios';

export const PLAN_PATH = '/sandbox/family-aa-claim-flows';

/**
 * The family-led AA claim on Loft's stage: phone frame or breakpoint, drill
 * mode, and every screen in the select. The one place it says it is a
 * prototype is here, in the chrome, so nothing inside the design has to.
 */
const Stage = () => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  // Loft's User switch: the family's side here, the adviser's on the other.
  const screenSelect = useScreenSelect();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sceneId, setSceneId] = useState(
    () => searchParams.get('scene') ?? aaScenes[0].id,
  );
  const scene = aaScenes.find((s) => s.id === sceneId) ?? aaScenes[0];
  // Which pathway the prototype runs: the same scenes, different people.
  const [scenario, setScenario] = useState<ScenarioId>(() => {
    const asked = searchParams.get('scenario');
    return SCENARIOS.some((s) => s.id === asked)
      ? (asked as ScenarioId)
      : 'standard';
  });
  const [shown, setShown] = useState<string>(scene.id);

  const follow = (frame: Frame) => {
    const at = sceneForFrame[frame];
    // Stay on a more specific scene of the same frame, e.g. a question.
    const same = aaScenes.find((s) => s.id === shown)?.initial.frame === frame;
    if (at && !same) setShown(at);
  };

  useEffect(() => {
    if (
      searchParams.get('scene') !== shown ||
      searchParams.get('scenario') !== scenario
    ) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', shown);
          next.set('scenario', scenario);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, scenario]);

  const [viewport, setViewport] = useState<Viewport>('frame');
  const framed = viewport === 'frame';
  const theme = useTheme();
  const desktop =
    typeof viewport === 'number' && viewport >= theme.breakpoints.values.md;
  const [fullEl, setFullEl] = useState<HTMLElement | null>(null);

  const exitDrillMode = useCallback(() => {
    setActive(false);
    setCurrent(null);
    setHovered(null);
  }, [setActive, setCurrent, setHovered]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        exitDrillMode();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, exitDrillMode, setCurrent, setHovered]);

  const pickScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setSceneId(id);
    setShown(id);
  };

  const flow = (
    <AaFamilyClaim
      key={`${scene.id}-${scenario}`}
      initial={{ ...scene.initial, scenario }}
      onFrameChange={follow}
    />
  );
  const screen = (
    <>
      {desktop ? <DesktopShell>{flow}</DesktopShell> : flow}
      <InspectOverlay />
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: framed ? 'center' : 'stretch',
            justifyContent: 'center',
            p: 3,
            pb: STAGE_BAR_ROOM,
            overflow: 'auto',
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              width: framed ? 'auto' : viewport === 'fill' ? '100%' : viewport,
              flex: viewport === 'fill' ? 1 : undefined,
              minHeight: 0,
            }}
            spacing={2}
          >
            {framed ? (
              <PhoneFrame inspecting={active}>{screen}</PhoneFrame>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  overflow: 'hidden',
                  borderRadius: `${radius.md}px`,
                  border: 1,
                  borderColor: 'custom.neutral.200',
                  bgcolor: 'background.default',
                }}
                ref={(el: HTMLElement | null) => setFullEl(el)}
              >
                <PhoneScreenContext.Provider value={fullEl}>
                  {screen}
                </PhoneScreenContext.Provider>
              </Box>
            )}
          </Stack>
        </Box>

        {/* The controls, floating at the foot of the stage. */}
        <StageBar>
          {screenSelect}
          <DrillModeToggle viewport={viewport} onViewportChange={setViewport} />
          <TextField
            select
            size='small'
            label={STAGE.scenario}
            // Full strength: Loft's grey label on the stage's cream measured 3.31:1.
            slotProps={{ inputLabel: { sx: { color: 'text.primary' } } }}
            value={scenario}
            onChange={(e) => setScenario(e.target.value as ScenarioId)}
            sx={{ minWidth: 180 }}
          >
            {SCENARIOS.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <StateSelector
            scenes={aaScenes}
            sceneId={shown}
            onSceneChange={pickScene}
            sections={AA_SECTIONS}
          />
          <Chip size='small' variant='outlined' label={STAGE.badge} />
          <Link component={RouterLink} to={PLAN_PATH} variant='body2'>
            {STAGE.about}
          </Link>
        </StageBar>
      </Box>

      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

const AaFamilyClaimStage = () => (
  <InspectProvider>
    <Stage />
  </InspectProvider>
);

export default AaFamilyClaimStage;
